import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalBody,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Flatpickr from "react-flatpickr";
import {
  GroupBase,
  OptionsOrGroups,
} from "react-select/dist/declarations/src/types";

import SelectAgency from "pages/Users/SelectAgency";
import SelectReportFile from "../RiskAssessments/SelectReportFile";
import { normalize } from "common/lib/normalize";
import {
  getAssessmentTypes,
} from "common/services/assessments.service";
import {
  getCapacityAssessmentRatings,
  getCapacityAssessmentTypes,
} from "common/services/capacity-assessments.service";
import { getRiskRatings } from "common/services/risk-assessments.service";
import { getContractors } from "common/services/contractors.service";
import { getUsersDefault } from "common/services/users.service";

type Option = {
  value: number | string | null;
  label: string;
};

export type AssessmentFormValues = {
  assessmentType: Option | null;
  title: string;
  contractor: Option | null;
  agency: Option | null;
  reportDate: Date | null;
  summary: string;
  conductedByUser: Option | null;
  conductedByContractor: Option | null;
  serialNumber: string;
  location: string;
  capacityAssessmentType: Option | null;
  overallRating: Option | null;
  overallRiskRating: Option | null;
  sensitivity: Option | null;
  classificationLevel: Option | null;
  noOfAssociatedEntitiesScreened: string;
  noofindividualsScreened: string;
  vettingEligibilityRecommendation: Option | null;
  verificationDate: Date | null;
  programmeRisk: Option | null;
  diversionRisk: Option | null;
  fiduciaryRisk: Option | null;
  integrityRisk: Option | null;
  fraudRisk: Option | null;
  reputationRisk: Option | null;
  humanRightsRisk: Option | null;
  terrorismRisk: Option | null;
  corruptionRisk: Option | null;
  obstructionRisk: Option | null;
  theftRisk: Option | null;
  partnerManagementRisk: Option | null;
  communityRelationsRisk: Option | null;
  selectedFiles: any[];
};

const selectStyles = (hasError: boolean) => ({
  control: (styles: any) => ({
    ...styles,
    borderColor: hasError ? "red" : "#ced4da",
    "&:hover": {
      borderColor: hasError ? "red" : "#ced4da",
    },
  }),
});

const SENSITIVITY_OPTIONS: Option[] = [
  { value: "Restricted", label: "Restricted" },
  { value: "Confidential", label: "Confidential" },
];

const CLASSIFICATION_LEVEL_OPTIONS: Option[] = [
  { value: "Confidential", label: "Confidential" },
  { value: "Internal Use Only", label: "Internal Use Only" },
  { value: "Strictly Confidential", label: "Strictly Confidential" },
];

const VETTING_RECOMMENDATION_OPTIONS: Option[] = [
  { value: "Favourable", label: "Favourable" },
  { value: "Unfavourable", label: "Unfavourable" },
];

const RISK_RATING_FIELDS: Array<{
  name: keyof AssessmentFormValues;
  label: string;
}> = [
  { name: "programmeRisk", label: "Programme Risk" },
  { name: "diversionRisk", label: "Diversion Risk" },
  { name: "fiduciaryRisk", label: "Fiduciary Risk" },
  { name: "integrityRisk", label: "Integrity Risk" },
  { name: "fraudRisk", label: "Fraud Risk" },
  { name: "reputationRisk", label: "Reputation Risk" },
  { name: "humanRightsRisk", label: "Human Rights Risk" },
  { name: "terrorismRisk", label: "Terrorism Risk" },
  { name: "corruptionRisk", label: "Corruption Risk" },
  { name: "obstructionRisk", label: "Obstruction Risk" },
  { name: "theftRisk", label: "Theft Risk" },
  { name: "partnerManagementRisk", label: "Partner Management Risk" },
  { name: "communityRelationsRisk", label: "Community Relations Risk" },
];

const createOption = (value: any, label?: string | null): Option | null => {
  if (!value) return null;
  return {
    value,
    label: label || "",
  };
};

const formatBytes = (bytes: number, decimals = 2) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const toNumberOrNull = (value: string) => {
  if (value === "" || value === null || value === undefined) return null;
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
};

const toDateString = (value: Date | null) =>
  value ? value.toLocaleDateString("en-CA") : null;

const requiredOption = (message: string) =>
  Yup.object()
    .nullable()
    .test("required-option", message, (value: any) => !!value?.value);

export const assessmentValidationSchema = Yup.object({
  assessmentType: requiredOption("Please select assessment type"),
  title: Yup.string().required("Please enter title"),
});

export const getAssessmentInitialValues = (selectedRecord?: any): AssessmentFormValues => ({
  assessmentType: createOption(
    selectedRecord?.assessmentType?.id,
    selectedRecord?.assessmentType?.name
  ),
  title: selectedRecord?.title || "",
  contractor: createOption(
    selectedRecord?.contractor?.id,
    selectedRecord?.contractor?.contractorName
      ? `${selectedRecord?.contractor?.contractorName} (${selectedRecord?.contractor?.acronym || "N/A"})`
      : ""
  ),
  agency: selectedRecord?.agency
    ? createOption(selectedRecord.agency.id, selectedRecord.agency.name)
    : { value: "", label: "" },
  reportDate: selectedRecord?.reportDate
    ? new Date(selectedRecord.reportDate)
    : null,
  summary: selectedRecord?.summary || "",
  conductedByUser: createOption(
    selectedRecord?.conductedByUser?.id,
    selectedRecord?.conductedByUser
      ? `${selectedRecord?.conductedByUser?.firstName || ""} ${
          selectedRecord?.conductedByUser?.lastName || ""
        }`.trim()
      : ""
  ),
  conductedByContractor: createOption(
    selectedRecord?.conductedByContractor?.id,
    selectedRecord?.conductedByContractor?.contractorName
      ? `${selectedRecord?.conductedByContractor?.contractorName} (${
          selectedRecord?.conductedByContractor?.acronym || "N/A"
        })`
      : ""
  ),
  serialNumber: selectedRecord?.serialNumber || "",
  location: selectedRecord?.location || "",
  capacityAssessmentType: createOption(
    selectedRecord?.capacityAssessmentType?.id,
    selectedRecord?.capacityAssessmentType?.name
  ),
  overallRating: createOption(
    selectedRecord?.overallRating?.id,
    selectedRecord?.overallRating?.name
  ),
  overallRiskRating: createOption(
    selectedRecord?.overallRiskRating?.id,
    selectedRecord?.overallRiskRating?.name
  ),
  sensitivity: createOption(selectedRecord?.sensitivity, selectedRecord?.sensitivity),
  classificationLevel: createOption(
    selectedRecord?.classificationLevel,
    selectedRecord?.classificationLevel
  ),
  noOfAssociatedEntitiesScreened:
    selectedRecord?.noOfAssociatedEntitiesScreened?.toString() || "",
  noofindividualsScreened:
    selectedRecord?.noofindividualsScreened?.toString() || "",
  vettingEligibilityRecommendation: createOption(
    selectedRecord?.vettingEligibilityRecommendation,
    selectedRecord?.vettingEligibilityRecommendation
  ),
  verificationDate: selectedRecord?.verificationDate
    ? new Date(selectedRecord.verificationDate)
    : null,
  programmeRisk: createOption(
    selectedRecord?.programmeRisk?.id,
    selectedRecord?.programmeRisk?.name
  ),
  diversionRisk: createOption(
    selectedRecord?.diversionRisk?.id,
    selectedRecord?.diversionRisk?.name
  ),
  fiduciaryRisk: createOption(
    selectedRecord?.fiduciaryRisk?.id,
    selectedRecord?.fiduciaryRisk?.name
  ),
  integrityRisk: createOption(
    selectedRecord?.integrityRisk?.id,
    selectedRecord?.integrityRisk?.name
  ),
  fraudRisk: createOption(selectedRecord?.fraudRisk?.id, selectedRecord?.fraudRisk?.name),
  reputationRisk: createOption(
    selectedRecord?.reputationRisk?.id,
    selectedRecord?.reputationRisk?.name
  ),
  humanRightsRisk: createOption(
    selectedRecord?.humanRightsRisk?.id,
    selectedRecord?.humanRightsRisk?.name
  ),
  terrorismRisk: createOption(
    selectedRecord?.terrorismRisk?.id,
    selectedRecord?.terrorismRisk?.name
  ),
  corruptionRisk: createOption(
    selectedRecord?.corruptionRisk?.id,
    selectedRecord?.corruptionRisk?.name
  ),
  obstructionRisk: createOption(
    selectedRecord?.obstructionRisk?.id,
    selectedRecord?.obstructionRisk?.name
  ),
  theftRisk: createOption(selectedRecord?.theftRisk?.id, selectedRecord?.theftRisk?.name),
  partnerManagementRisk: createOption(
    selectedRecord?.partnerManagementRisk?.id,
    selectedRecord?.partnerManagementRisk?.name
  ),
  communityRelationsRisk: createOption(
    selectedRecord?.communityRelationsRisk?.id,
    selectedRecord?.communityRelationsRisk?.name
  ),
  selectedFiles:
    selectedRecord?.reportFile?.map((file: any) => ({
      id: file.id,
      name: file.name,
      formattedSize: file.formattedSize || formatBytes(file.size),
    })) || [],
});

export const buildAssessmentFormData = (
  values: AssessmentFormValues,
  isUpdate = false
) => {
  const data: any = {
    assessmentType: values.assessmentType?.value || null,
    title: values.title,
    contractor: values.contractor?.value || null,
    agency: values.agency?.value || null,
    reportDate: toDateString(values.reportDate),
    summary: values.summary,
    conductedByUser: values.conductedByUser?.value || null,
    conductedByContractor: values.conductedByContractor?.value || null,
    serialNumber: values.serialNumber || null,
    location: values.location || null,
    capacityAssessmentType: values.capacityAssessmentType?.value || null,
    overallRating: values.overallRating?.value || null,
    overallRiskRating: values.overallRiskRating?.value || null,
    sensitivity: values.sensitivity?.value || null,
    classificationLevel: values.classificationLevel?.value || null,
    noOfAssociatedEntitiesScreened: toNumberOrNull(
      values.noOfAssociatedEntitiesScreened
    ),
    noofindividualsScreened: toNumberOrNull(values.noofindividualsScreened),
    vettingEligibilityRecommendation:
      values.vettingEligibilityRecommendation?.value || null,
    verificationDate: toDateString(values.verificationDate),
    programmeRisk: values.programmeRisk?.value || null,
    diversionRisk: values.diversionRisk?.value || null,
    fiduciaryRisk: values.fiduciaryRisk?.value || null,
    integrityRisk: values.integrityRisk?.value || null,
    fraudRisk: values.fraudRisk?.value || null,
    reputationRisk: values.reputationRisk?.value || null,
    humanRightsRisk: values.humanRightsRisk?.value || null,
    terrorismRisk: values.terrorismRisk?.value || null,
    corruptionRisk: values.corruptionRisk?.value || null,
    obstructionRisk: values.obstructionRisk?.value || null,
    theftRisk: values.theftRisk?.value || null,
    partnerManagementRisk: values.partnerManagementRisk?.value || null,
    communityRelationsRisk: values.communityRelationsRisk?.value || null,
  };

  if (isUpdate) {
    data.reportFile = values.selectedFiles
      .filter((file: any) => !!file?.id)
      .map((file: any) => file.id);
  }

  const formData = new FormData();
  values.selectedFiles.forEach((file: any) => {
    if (!file?.id) {
      formData.append("files.reportFile", file, file.name);
    }
  });
  formData.append("data", JSON.stringify(data));

  return formData;
};

const BasicSelectField = ({
  name,
  label,
  placeholder,
  options,
  validation,
  isLoading = false,
  isDisabled = false,
}: {
  name: keyof AssessmentFormValues;
  label: string;
  placeholder: string;
  options: Option[];
  validation: any;
  isLoading?: boolean;
  isDisabled?: boolean;
}) => {
  const hasError = !!(validation.touched[name] && validation.errors[name]);

  return (
    <FormGroup>
      <Label className="form-label" htmlFor={`${String(name)}-field`}>
        {label}
      </Label>
      <Select
        inputId={`${String(name)}-field`}
        name={String(name)}
        value={validation.values[name] || null}
        onChange={(value: any) => {
          validation.setFieldValue(name, value);
          validation.setFieldError(name, "");
        }}
        onBlur={() => validation.setFieldTouched(name, true)}
        options={options}
        placeholder={placeholder}
        styles={selectStyles(hasError)}
        className={hasError ? "is-invalid" : ""}
        isLoading={isLoading}
        isDisabled={isDisabled}
        isClearable
      />
      <FormFeedback>{validation.errors[name]?.value || validation.errors[name]}</FormFeedback>
    </FormGroup>
  );
};

const AsyncContractorSelectField = ({
  name,
  label,
  placeholder,
  validation,
  onValueChange,
}: {
  name: "contractor" | "conductedByContractor";
  label: string;
  placeholder: string;
  validation: any;
  onValueChange?: (value: Option | null) => void;
}) => {
  const [defaultOptions, setDefaultOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const hasError = !!(validation.touched[name] && validation.errors[name]);

  useEffect(() => {
    const fetchDefaultOptions = async () => {
      try {
        setLoading(true);
        const response: any = await getContractors({
          pagination: {
            page: 1,
            pageSize: 20,
          },
          sort: ["contractorName"],
        });

        const data = normalize(response.data);
        setDefaultOptions(
          data.map((item: any) => ({
            value: item.id,
            label: `${item.contractorName} (${item.acronym || "N/A"})`,
          }))
        );
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultOptions();
  }, []);

  return (
    <FormGroup>
      <Label className="form-label" htmlFor={`${name}-field`}>
        {label}
      </Label>
      <AsyncSelect
        cacheOptions
        defaultOptions={defaultOptions}
        isLoading={loading}
        name={name}
        inputId={`${name}-field`}
        value={validation.values[name] || null}
        // @ts-ignore
        loadOptions={(
          inputValue: string,
          callback: (options: OptionsOrGroups<any, GroupBase<any>>) => void
        ) => {
          getContractors({
            pagination: {
              page: 1,
              pageSize: 20,
            },
            filters: {
              $or: [
                {
                  contractorName: {
                    $containsi: inputValue,
                  },
                },
                {
                  acronym: {
                    $containsi: inputValue,
                  },
                },
              ],
            },
          }).then((result: any) => {
            const parsed = normalize(result.data);
            callback(
              parsed.map((item: any) => ({
                value: item.id,
                label: `${item.contractorName} (${item.acronym || "N/A"})`,
              }))
            );
          });
        }}
        onChange={(value: any) => {
          validation.setFieldValue(name, value);
          validation.setFieldError(name, "");
          onValueChange?.(value);
        }}
        onBlur={() => validation.setFieldTouched(name, true)}
        placeholder={placeholder}
        styles={selectStyles(hasError)}
        className={hasError ? "is-invalid" : ""}
        isClearable
      />
      <FormFeedback>{validation.errors[name]?.value || validation.errors[name]}</FormFeedback>
    </FormGroup>
  );
};

const AsyncUserSelectField = ({
  validation,
  onValueChange,
}: {
  validation: any;
  onValueChange?: (value: Option | null) => void;
}) => {
  const name = "conductedByUser";
  const [defaultOptions, setDefaultOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const hasError = !!(validation.touched[name] && validation.errors[name]);

  useEffect(() => {
    const fetchDefaultOptions = async () => {
      try {
        setLoading(true);
        const response: any = await getUsersDefault({
          pagination: {
            page: 1,
            pageSize: 50,
          },
        });

        const data = normalize(response.data);
        setDefaultOptions(
          data.map((item: any) => ({
            value: item.id,
            label: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
          }))
        );
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultOptions();
  }, []);

  return (
    <FormGroup>
      <Label className="form-label" htmlFor="conductedByUser-field">
        Conducted By User
      </Label>
      <AsyncSelect
        cacheOptions
        defaultOptions={defaultOptions}
        isLoading={loading}
        inputId="conductedByUser-field"
        value={validation.values.conductedByUser || null}
        // @ts-ignore
        loadOptions={(
          inputValue: string,
          callback: (options: OptionsOrGroups<any, GroupBase<any>>) => void
        ) => {
          getUsersDefault({
            pagination: {
              page: 1,
              pageSize: 50,
            },
            filters: {
              $or: [
                {
                  firstName: {
                    $containsi: inputValue,
                  },
                },
                {
                  lastName: {
                    $containsi: inputValue,
                  },
                },
              ],
            },
          }).then((result: any) => {
            const parsed = normalize(result.data);
            callback(
              parsed.map((item: any) => ({
                value: item.id,
                label: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
              }))
            );
          });
        }}
        onChange={(value: any) => {
          validation.setFieldValue(name, value);
          validation.setFieldError(name, "");
          onValueChange?.(value);
        }}
        onBlur={() => validation.setFieldTouched(name, true)}
        placeholder="Select user"
        styles={selectStyles(hasError)}
        className={hasError ? "is-invalid" : ""}
        isClearable
      />
      <FormFeedback>{validation.errors[name]?.value || validation.errors[name]}</FormFeedback>
    </FormGroup>
  );
};

const AssessmentForm: React.FC<{
  toggle: () => void;
  loading: boolean;
  isUpdate?: boolean;
  validation: ReturnType<typeof useFormik<AssessmentFormValues>>;
}> = ({ validation, toggle, isUpdate, loading }) => {
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [assessmentTypeOptions, setAssessmentTypeOptions] = useState<Option[]>([]);
  const [capacityAssessmentTypeOptions, setCapacityAssessmentTypeOptions] = useState<
    Option[]
  >([]);
  const [capacityAssessmentRatingOptions, setCapacityAssessmentRatingOptions] = useState<
    Option[]
  >([]);
  const [riskRatingOptions, setRiskRatingOptions] = useState<Option[]>([]);

  const isCapacityAssessment =
    validation.values.assessmentType?.label?.trim().toLowerCase() ===
    "capacity assessment";
  const hasCapacityType = !!validation.values.capacityAssessmentType?.value;

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setOptionsLoading(true);
        const [assessmentTypesResponse, capacityTypesResponse, riskRatingsResponse] =
          await Promise.all([
            getAssessmentTypes({
              pagination: {
                page: 1,
                pageSize: 100,
              },
              sort: ["name"],
            }),
            getCapacityAssessmentTypes({
              pagination: {
                page: 1,
                pageSize: 100,
              },
              sort: ["name"],
            }),
            getRiskRatings({
              pagination: {
                page: 1,
                pageSize: 100,
              },
              sort: ["name"],
            }),
          ]);

        setAssessmentTypeOptions(
          normalize(assessmentTypesResponse.data).map((item: any) => ({
            value: item.id,
            label: item.name,
          }))
        );
        setCapacityAssessmentTypeOptions(
          normalize(capacityTypesResponse.data).map((item: any) => ({
            value: item.id,
            label: item.name,
          }))
        );
        setRiskRatingOptions(
          normalize(riskRatingsResponse.data).map((item: any) => ({
            value: item.id,
            label: item.name,
          }))
        );
      } catch (error) {
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (
      !isCapacityAssessment &&
      (validation.values.capacityAssessmentType || validation.values.overallRating)
    ) {
      validation.setFieldValue("capacityAssessmentType", null);
      validation.setFieldValue("overallRating", null);
    }
  }, [
    isCapacityAssessment,
    validation,
    validation.values.capacityAssessmentType,
    validation.values.overallRating,
  ]);

  useEffect(() => {
    const fetchCapacityRatings = async () => {
      if (!isCapacityAssessment || !validation.values.capacityAssessmentType?.value) {
        setCapacityAssessmentRatingOptions([]);
        return;
      }

      try {
        const response: any = await getCapacityAssessmentRatings({
          filters: {
            capacityAssessmentType: validation.values.capacityAssessmentType.value,
          },
          pagination: {
            page: 1,
            pageSize: 100,
          },
          sort: ["name"],
        });

        setCapacityAssessmentRatingOptions(
          normalize(response.data).map((item: any) => ({
            value: item.id,
            label: item.name,
          }))
        );
      } catch (error) {
        setCapacityAssessmentRatingOptions([]);
      }
    };

    fetchCapacityRatings();
  }, [isCapacityAssessment, validation.values.capacityAssessmentType]);

  const riskFields = useMemo(() => RISK_RATING_FIELDS, []);

  return (
    <Form
      className="tablelist-form"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      <ModalBody>
        <div className="mb-3">
          <Label htmlFor="title-field" className="form-label">
            Title
          </Label>
          <Input
            id="title-field"
            name="title"
            type="text"
            className="form-control"
            placeholder="Enter assessment title"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.title}
            invalid={!!(validation.touched.title && validation.errors.title)}
          />
          <FormFeedback>{validation.errors.title}</FormFeedback>
        </div>

        <Row>
          <Col md={6}>
            <BasicSelectField
              name="assessmentType"
              label="Assessment Type"
              placeholder="Select assessment type"
              options={assessmentTypeOptions}
              validation={validation}
              isLoading={optionsLoading}
            />
          </Col>
          <Col md={6}>
            <SelectAgency validation={validation} />
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <AsyncContractorSelectField
              name="contractor"
              label="Contractor"
              placeholder="Select contractor"
              validation={validation}
            />
          </Col>
        </Row>

        <div className="border rounded p-3 mb-3">
          <h5 className="fs-14 mb-1">Conducted By</h5>
          <p className="text-muted mb-3">
            Choose one option. The assessment can be conducted by an existing
            staff user or by an external contractor such as an audit firm.
          </p>
          <Row>
            <Col md={6}>
              <AsyncUserSelectField
                validation={validation}
                onValueChange={(value) => {
                  if (value) {
                    validation.setFieldValue("conductedByContractor", null);
                    validation.setFieldTouched("conductedByContractor", false);
                  }
                }}
              />
            </Col>
            <Col md={6}>
              <AsyncContractorSelectField
                name="conductedByContractor"
                label="Conducted By Contractor"
                placeholder="Select contractor"
                validation={validation}
                onValueChange={(value) => {
                  if (value) {
                    validation.setFieldValue("conductedByUser", null);
                    validation.setFieldTouched("conductedByUser", false);
                  }
                }}
              />
            </Col>
          </Row>
        </div>

        {isCapacityAssessment && (
          <Row>
            <Col md={6}>
              <BasicSelectField
                name="capacityAssessmentType"
                label="Capacity Assessment Type"
                placeholder="Select capacity assessment type"
                options={capacityAssessmentTypeOptions}
                validation={validation}
                isLoading={optionsLoading}
              />
            </Col>
          </Row>
        )}

        <Row>
          {isCapacityAssessment && (
            <Col md={6}>
              <BasicSelectField
                name="overallRating"
                label="Overall Capacity Rating"
                placeholder="Select overall rating"
                options={capacityAssessmentRatingOptions}
                validation={validation}
                isDisabled={!hasCapacityType}
              />
            </Col>
          )}
          <Col md={6}>
            <BasicSelectField
              name="overallRiskRating"
              label="Overall Risk Rating"
              placeholder="Select overall risk rating"
              options={riskRatingOptions}
              validation={validation}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mb-3">
            <Label htmlFor="reportDate-field" className="form-label">
              Report Date
            </Label>
            {/* @ts-ignore */}
            <Flatpickr
              id="reportDate-field"
              className="form-control"
              placeholder="Select report date"
              options={{
                enableTime: false,
                altInput: false,
                altFormat: "d M, Y",
                dateFormat: "d M, Y",
              }}
              onChange={(dates) => {
                validation.setFieldValue("reportDate", dates[0] || null);
              }}
              value={validation.values.reportDate || ""}
            />
          </Col>
          <Col md={6} className="mb-3">
            <Label htmlFor="verificationDate-field" className="form-label">
              Verification Date
            </Label>
            {/* @ts-ignore */}
            <Flatpickr
              id="verificationDate-field"
              className="form-control"
              placeholder="Select verification date"
              options={{
                enableTime: false,
                altInput: false,
                altFormat: "d M, Y",
                dateFormat: "d M, Y",
              }}
              onChange={(dates) => {
                validation.setFieldValue("verificationDate", dates[0] || null);
              }}
              value={validation.values.verificationDate || ""}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mb-3">
            <Label htmlFor="serialNumber-field" className="form-label">
              Serial Number
            </Label>
            <Input
              id="serialNumber-field"
              name="serialNumber"
              type="text"
              className="form-control"
              placeholder="Enter serial number"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.serialNumber}
            />
          </Col>
          <Col md={6} className="mb-3">
            <Label htmlFor="location-field" className="form-label">
              Location
            </Label>
            <Input
              id="location-field"
              name="location"
              type="text"
              className="form-control"
              placeholder="Enter location"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.location}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <BasicSelectField
              name="sensitivity"
              label="Sensitivity"
              placeholder="Select sensitivity"
              options={SENSITIVITY_OPTIONS}
              validation={validation}
            />
          </Col>
          <Col md={6}>
            <BasicSelectField
              name="classificationLevel"
              label="Classification Level"
              placeholder="Select classification level"
              options={CLASSIFICATION_LEVEL_OPTIONS}
              validation={validation}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mb-3">
            <Label htmlFor="associated-entities-field" className="form-label">
              No. of Associated Entities Screened
            </Label>
            <Input
              id="associated-entities-field"
              name="noOfAssociatedEntitiesScreened"
              type="number"
              min={0}
              className="form-control"
              placeholder="Enter number screened"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.noOfAssociatedEntitiesScreened}
            />
          </Col>
          <Col md={6} className="mb-3">
            <Label htmlFor="individuals-screened-field" className="form-label">
              No. of Individuals Screened
            </Label>
            <Input
              id="individuals-screened-field"
              name="noofindividualsScreened"
              type="number"
              min={0}
              className="form-control"
              placeholder="Enter number screened"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.noofindividualsScreened}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <BasicSelectField
              name="vettingEligibilityRecommendation"
              label="Vetting Eligibility Recommendation"
              placeholder="Select recommendation"
              options={VETTING_RECOMMENDATION_OPTIONS}
              validation={validation}
            />
          </Col>
        </Row>

        <Row>
          <Col md={12} className="mb-3">
            <Label htmlFor="summary-field" className="form-label">
              Summary
            </Label>
            <Input
              id="summary-field"
              name="summary"
              type="textarea"
              className="form-control"
              placeholder="Enter assessment summary"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.summary}
              rows={4}
            />
          </Col>
        </Row>

        <div className="border rounded p-3 mb-3">
          <h5 className="fs-14 mb-3">Detailed Risk Ratings</h5>
          <Row>
            {riskFields.map((field) => (
              <Col md={4} key={String(field.name)}>
                <BasicSelectField
                  name={field.name}
                  label={field.label}
                  placeholder={`Select ${field.label.toLowerCase()}`}
                  options={riskRatingOptions}
                  validation={validation}
                />
              </Col>
            ))}
          </Row>
        </div>

        <Row>
          <SelectReportFile validation={validation} />
        </Row>
      </ModalBody>

      <div className="modal-footer">
        <div className="hstack gap-2 justify-content-end">
          <button type="button" className="btn btn-light" onClick={toggle}>
            Close
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading && (
              <Spinner
                color="light"
                size="sm"
                style={{ marginRight: "8px", marginBottom: "-1px" }}
              />
            )}
            {isUpdate ? "Update Assessment" : "Add Assessment"}
          </button>
        </div>
      </div>
    </Form>
  );
};

export default AssessmentForm;
